import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, Toast, UIResources, SiteContent } from '../types';

// Default translations (moved from SiteContext or imported)
// For brevity, I will assume we can fetch them or keep them here.
// To avoid massive duplication, I'll keep the translations object here or in a separate file `translations.ts`.
// I'll create `translations.ts` next.

import { translations } from '../translations';
import { contentTR, contentEN, contentIT } from '../content'; // We need to extract content too

interface UIContextType {
    theme: 'dark' | 'light';
    toggleTheme: () => void;
    language: Language;
    setLanguage: (lang: Language) => void;
    t: UIResources;
    toasts: Toast[];
    addToast: (message: string, type?: 'success' | 'error' | 'info') => void;
    removeToast: (id: number) => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const useUI = () => {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error('useUI must be used within a UIProvider');
    }
    return context;
};

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Theme
    const [theme, setTheme] = useState<'dark' | 'light'>(() => {
        try {
            const saved = localStorage.getItem('greysoft_theme');
            if (saved === 'dark' || saved === 'light') return saved;
        } catch { }
        return 'dark';
    });

    useEffect(() => {
        try { localStorage.setItem('greysoft_theme', theme); } catch { }
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    };

    // Language
    const [language, setLanguageState] = useState<Language>(() => {
        try { return (localStorage.getItem('greysoft_lang') as Language) || 'it'; } catch { return 'it'; }
    });

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem('greysoft_lang', lang);
    };

    const t = translations[language];

    // Toasts
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => { removeToast(id); }, 3000);
    };

    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return (
        <UIContext.Provider value={{ theme, toggleTheme, language, setLanguage, t, toasts, addToast, removeToast }}>
            {children}
        </UIContext.Provider>
    );
};
